'use client';

import { useState, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface QuizQuestion {
  id: number;
  difficulty: 'easy' | 'medium' | 'hard';
  topic: string;
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

const allQuestions: QuizQuestion[] = [
  {
    id: 1, difficulty: 'easy', topic: 'Cell Structure',
    question: 'Which organelle is responsible for producing ATP through cellular respiration?',
    options: ['Chloroplast', 'Mitochondria', 'Ribosome', 'Golgi apparatus'],
    correctIndex: 1,
    explanation: 'Mitochondria are the "powerhouses" of the cell, generating ATP via the electron transport chain and oxidative phosphorylation.'
  },
  {
    id: 2, difficulty: 'easy', topic: 'Photosynthesis',
    question: 'In which part of the chloroplast do the light-dependent reactions occur?',
    options: ['Stroma', 'Thylakoid membrane', 'Outer membrane', 'Matrix'],
    correctIndex: 1,
    explanation: 'Light-dependent reactions occur in the thylakoid membranes, where chlorophyll absorbs light to drive photophosphorylation.'
  },
  {
    id: 3, difficulty: 'easy', topic: 'DNA & Genetics',
    question: 'Which base pairs with adenine in DNA?',
    options: ['Cytosine', 'Guanine', 'Thymine', 'Uracil'],
    correctIndex: 2,
    explanation: 'In DNA, adenine (A) pairs with thymine (T) via two hydrogen bonds. In RNA, adenine pairs with uracil.'
  },
  {
    id: 4, difficulty: 'medium', topic: 'Cellular Respiration',
    question: 'Where does the Krebs cycle take place?',
    options: ['Cytoplasm', 'Mitochondrial matrix', 'Inner mitochondrial membrane', 'Nucleus'],
    correctIndex: 1,
    explanation: 'The Krebs (citric acid) cycle occurs in the mitochondrial matrix, where acetyl-CoA is oxidized to produce NADH, FADH₂, and CO₂.'
  },
  {
    id: 5, difficulty: 'medium', topic: 'Cell Signaling',
    question: 'A hormone binds to a receptor on the cell surface. What type of signaling is this?',
    options: ['Autocrine', 'Paracrine', 'Endocrine', 'Juxtacrine'],
    correctIndex: 2,
    explanation: 'Endocrine signaling involves hormones traveling through the bloodstream to reach distant target cells with surface receptors.'
  },
  {
    id: 6, difficulty: 'medium', topic: 'Evolution',
    question: 'Which of the following is NOT a condition required for Hardy-Weinberg equilibrium?',
    options: ['Large population size', 'No mutation', 'Natural selection occurring', 'Random mating'],
    correctIndex: 2,
    explanation: 'Hardy-Weinberg equilibrium requires NO natural selection. When selection acts on alleles, allele frequencies change across generations.'
  },
  {
    id: 7, difficulty: 'medium', topic: 'Molecular Biology',
    question: 'During translation, which molecule reads the mRNA codon?',
    options: ['DNA polymerase', 'tRNA anticodon', 'rRNA', 'snRNA'],
    correctIndex: 1,
    explanation: 'tRNA carries an anticodon that base-pairs with the mRNA codon, bringing the correct amino acid to the ribosome for peptide bond formation.'
  },
  {
    id: 8, difficulty: 'medium', topic: 'Ecology',
    question: 'Primary productivity is HIGHEST in which ecosystem?',
    options: ['Temperate forest', 'Open ocean', 'Tropical rainforest', 'Arctic tundra'],
    correctIndex: 2,
    explanation: 'Tropical rainforests have the highest net primary productivity due to year-round warmth, sunlight, and rainfall supporting dense vegetation.'
  },
  {
    id: 9, difficulty: 'hard', topic: 'Cellular Respiration',
    question: 'A cell is treated with oligomycin, which blocks ATP synthase. What would you expect?',
    options: [
      'Increased O₂ consumption',
      'Decreased proton gradient across inner membrane',
      'Increased proton gradient, decreased ATP synthesis',
      'No effect on electron transport chain'
    ],
    correctIndex: 2,
    explanation: 'Blocking ATP synthase prevents protons from flowing through it, causing the proton gradient to build up (increase). The ETC slows as the gradient becomes too steep, and ATP synthesis stops.'
  },
  {
    id: 10, difficulty: 'hard', topic: 'Genetics',
    question: 'In a dihybrid cross (AaBb × AaBb), what fraction of offspring would be aaB_?',
    options: ['3/16', '1/4', '3/4', '1/16'],
    correctIndex: 0,
    explanation: 'P(aa) = 1/4, P(B_) = 3/4. Combined: 1/4 × 3/4 = 3/16. This assumes independent assortment (Mendel\'s second law).'
  },
  {
    id: 11, difficulty: 'hard', topic: 'Molecular Biology',
    question: 'A mutation in the -10 box of a bacterial promoter would most likely result in:',
    options: [
      'Altered amino acid sequence',
      'Decreased transcription initiation',
      'Increased translation efficiency',
      'Premature termination of translation'
    ],
    correctIndex: 1,
    explanation: 'The -10 box (Pribnow box) is a conserved promoter element where RNA polymerase binds. Mutations here reduce binding affinity and decrease transcription initiation frequency.'
  },
  {
    id: 12, difficulty: 'hard', topic: 'Experimental Design',
    question: 'Researchers add a labeled carbon (¹⁴C) to CO₂ and expose it to a plant. After 30 seconds, ¹⁴C is found highest in 3-phosphoglycerate. What does this indicate?',
    options: [
      'The Calvin cycle is not occurring',
      'CO₂ was fixed into RuBP by RuBisCO, then immediately converted to 3-PGA',
      'Photorespiration is the dominant pathway',
      'Light reactions are producing excess ATP'
    ],
    correctIndex: 1,
    explanation: 'This describes the Calvin-Benson cycle: CO₂ + RuBP → 2 molecules of 3-PGA via RuBisCO. The rapid appearance of ¹⁴C in 3-PGA confirms carbon fixation is occurring normally.'
  },
];

interface TopicScore {
  topic: string;
  correct: number;
  total: number;
  zone: 'mastery' | 'review' | 'gap';
}

type QuizPhase = 'intro' | 'question' | 'explanation' | 'results';

export default function DiagnosticQuiz() {
  const [phase, setPhase] = useState<QuizPhase>('intro');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [answers, setAnswers] = useState<{ questionId: number; correct: boolean; topic: string }[]>([]);
  const [topicScores, setTopicScores] = useState<TopicScore[]>([]);
  const quizRef = useRef<HTMLDivElement>(null);

  const currentQuestion = allQuestions[currentIndex];
  const progress = ((currentIndex) / allQuestions.length) * 100;

  const scrollToQuiz = () => {
    quizRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSelectOption = (index: number) => {
    if (selectedOption !== null) return;
    setSelectedOption(index);
  };

  const handleNext = () => {
    if (selectedOption === null) return;

    const isCorrect = selectedOption === currentQuestion.correctIndex;
    const newAnswers = [...answers, { questionId: currentQuestion.id, correct: isCorrect, topic: currentQuestion.topic }];
    setAnswers(newAnswers);

    if (currentIndex < allQuestions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedOption(null);
      setPhase('question');
      setTimeout(scrollToQuiz, 100);
    } else {
      // Calculate topic scores
      const topicMap: Record<string, { correct: number; total: number }> = {};
      newAnswers.forEach(a => {
        if (!topicMap[a.topic]) topicMap[a.topic] = { correct: 0, total: 0 };
        topicMap[a.topic].total++;
        if (a.correct) topicMap[a.topic].correct++;
      });

      const scores: TopicScore[] = Object.entries(topicMap).map(([topic, { correct, total }]) => {
        const pct = correct / total;
        return {
          topic,
          correct,
          total,
          zone: pct >= 0.75 ? 'mastery' : pct >= 0.5 ? 'review' : 'gap',
        };
      });

      setTopicScores(scores);
      setPhase('results');
      setTimeout(scrollToQuiz, 100);
    }
  };

  const getOptionClass = (index: number) => {
    if (selectedOption === null) return 'quiz-option border border-cream-darker bg-cream';
    if (index === currentQuestion.correctIndex) return 'quiz-option correct border-2';
    if (index === selectedOption && selectedOption !== currentQuestion.correctIndex) return 'quiz-option incorrect border-2';
    return 'quiz-option border border-cream-darker bg-cream opacity-60';
  };

  const totalCorrect = answers.filter(a => a.correct).length;
  const scorePercent = Math.round((totalCorrect / allQuestions.length) * 100);

  return (
    <section id="quiz" ref={quizRef} className="py-28 px-6 md:px-12 bg-cream-dark">
      <div className="max-w-3xl mx-auto">
        {phase === 'intro' && (
          <div className="text-center animate-scale-in">
            <span className="text-xs font-semibold tracking-widest uppercase text-amber block mb-4">
              12-Question Diagnostic
            </span>
            <h2 className="font-fraunces text-5xl md:text-6xl font-light text-ink mb-6 leading-tight">
              Find Your<br />
              <span className="italic text-green">Weak Spots.</span>
            </h2>
            <p className="text-ink-muted text-lg font-light max-w-xl mx-auto mb-8 leading-relaxed">
              Questions start with organelle identification and escalate to experimental prediction.
              Takes about 7 minutes. You'll get a personal topic map at the end — green zones, amber zones, and the mauve gaps worth your attention.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-10 text-sm text-ink-muted">
              {['Cell Structure', 'Photosynthesis', 'Cellular Respiration', 'Genetics', 'Ecology', 'Molecular Bio'].map(t => (
                <span key={t} className="px-3 py-1 rounded-full bg-green-pale border border-green text-green text-xs font-medium">{t}</span>
              ))}
            </div>
            <button
              onClick={() => { setPhase('question'); setTimeout(scrollToQuiz, 100); }}
              className="btn-amber px-10 py-4 rounded-full text-base inline-flex items-center gap-3"
            >
              <Icon name="MagnifyingGlassIcon" size={18} />
              Find Your Weak Spots
            </button>
            <p className="text-xs text-ink-muted mt-4">No signup required. Results appear inline.</p>
          </div>
        )}

        {(phase === 'question' || phase === 'explanation') && (
          <div className="animate-scale-in">
            {/* Progress */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-semibold text-ink-muted">Question {currentIndex + 1} of {allQuestions.length}</span>
                <span className="text-xs font-semibold text-mauve capitalize">{currentQuestion.topic}</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }} />
              </div>
            </div>

            {/* Difficulty badge */}
            <div className="mb-6">
              <span className={`text-xs font-semibold px-3 py-1 rounded-full capitalize ${
                currentQuestion.difficulty === 'easy' ? 'bg-green-pale text-green' :
                currentQuestion.difficulty === 'medium'? 'bg-amber-pale text-amber-dark' : 'bg-mauve-pale text-mauve'
              }`}>
                {currentQuestion.difficulty}
              </span>
            </div>

            {/* Question */}
            <div className="bg-cream rounded-3xl p-8 mb-6 border border-cream-darker">
              <h3 className="font-fraunces text-2xl font-medium text-ink leading-snug">
                {currentQuestion.question}
              </h3>
            </div>

            {/* Options */}
            <div className="space-y-3 mb-6">
              {currentQuestion.options.map((option, i) => (
                <button
                  key={i}
                  onClick={() => handleSelectOption(i)}
                  className={`w-full text-left p-4 rounded-2xl flex items-center gap-4 ${getOptionClass(i)}`}
                >
                  <span className={`w-8 h-8 rounded-full border flex-shrink-0 flex items-center justify-center text-sm font-semibold ${
                    selectedOption !== null && i === currentQuestion.correctIndex ? 'border-green bg-green text-white' :
                    selectedOption === i && i !== currentQuestion.correctIndex ? 'border-red-400 bg-red-100 text-red-700': 'border-cream-darker text-ink-muted'
                  }`}>
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span className="text-sm font-medium text-ink">{option}</span>
                  {selectedOption !== null && i === currentQuestion.correctIndex && (
                    <Icon name="CheckCircleIcon" size={18} className="ml-auto text-green" variant="solid" />
                  )}
                  {selectedOption === i && i !== currentQuestion.correctIndex && (
                    <Icon name="XCircleIcon" size={18} className="ml-auto text-red-500" variant="solid" />
                  )}
                </button>
              ))}
            </div>

            {/* Explanation */}
            {selectedOption !== null && (
              <div className="bg-amber-pale border border-amber rounded-2xl p-5 mb-6 animate-slide-up">
                <div className="flex items-start gap-3">
                  <Icon name="LightBulbIcon" size={18} className="text-amber-dark flex-shrink-0 mt-0.5" variant="solid" />
                  <div>
                    <p className="text-xs font-semibold text-amber-dark mb-1 uppercase tracking-wide">Explanation</p>
                    <p className="text-sm text-ink-light leading-relaxed">{currentQuestion.explanation}</p>
                  </div>
                </div>
              </div>
            )}

            {selectedOption !== null && (
              <button
                onClick={handleNext}
                className="btn-amber w-full py-4 rounded-2xl text-base flex items-center justify-center gap-2"
              >
                {currentIndex < allQuestions.length - 1 ? (
                  <>Next Question <Icon name="ArrowRightIcon" size={16} /></>
                ) : (
                  <>See My Topic Map <Icon name="ChartBarIcon" size={16} /></>
                )}
              </button>
            )}
          </div>
        )}

        {phase === 'results' && (
          <div className="animate-scale-in">
            <div className="text-center mb-10">
              <span className="text-xs font-semibold tracking-widest uppercase text-green block mb-4">Your Results</span>
              <h2 className="font-fraunces text-4xl md:text-5xl font-light text-ink mb-4">
                Your Personal<br />
                <span className="italic text-green">Topic Map.</span>
              </h2>
              <p className="text-ink-muted font-light">
                {totalCorrect} of {allQuestions.length} correct — {scorePercent}% overall
              </p>
            </div>

            {/* Score ring visual */}
            <div className="flex justify-center mb-10">
              <div className="relative w-32 h-32">
                <svg viewBox="0 0 120 120" className="w-32 h-32 -rotate-90">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="#E0D8C8" strokeWidth="10" />
                  <circle
                    cx="60" cy="60" r="50"
                    fill="none"
                    stroke={scorePercent >= 75 ? '#4A7C59' : scorePercent >= 50 ? '#D4A03C' : '#9B7A8F'}
                    strokeWidth="10"
                    strokeDasharray={`${(scorePercent / 100) * 314} 314`}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dasharray 1s cubic-bezier(0.16,1,0.3,1)' }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="font-fraunces text-2xl font-semibold text-ink">{scorePercent}%</span>
                  <span className="text-xs text-ink-muted">overall</span>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="flex justify-center gap-6 mb-8 text-xs font-medium">
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-green inline-block" />Mastery (≥75%)</div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-amber inline-block" />Review (50–74%)</div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-mauve inline-block" />Gap (&lt;50%)</div>
            </div>

            {/* Topic cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
              {topicScores.map((ts) => (
                <div
                  key={ts.topic}
                  className={`rounded-2xl border-2 p-4 zone-${ts.zone} flex items-center justify-between`}
                >
                  <div>
                    <p className="font-semibold text-sm text-ink">{ts.topic}</p>
                    <p className="text-xs text-ink-muted mt-0.5">{ts.correct}/{ts.total} correct</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${
                      ts.zone === 'mastery' ? 'bg-green text-white' :
                      ts.zone === 'review'? 'bg-amber text-ink' : 'bg-mauve text-white'
                    }`}>
                      {ts.zone}
                    </span>
                    <span className="text-lg font-fraunces font-semibold text-ink">
                      {Math.round((ts.correct / ts.total) * 100)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-green-pale border border-green rounded-3xl p-6 text-center">
              <p className="font-fraunces text-xl font-medium text-ink mb-2">
                Ready to close those gaps?
              </p>
              <p className="text-ink-muted text-sm font-light mb-4">
                Enter your name and exam date — we'll generate a sequenced prep calendar targeting your mauve zones first.
              </p>
              <a href="#plan" className="btn-amber px-8 py-3 rounded-full text-sm inline-flex items-center gap-2">
                Start Your Study Plan <Icon name="ArrowRightIcon" size={14} />
              </a>
            </div>

            <button
              onClick={() => {
                setPhase('intro');
                setCurrentIndex(0);
                setSelectedOption(null);
                setAnswers([]);
                setTopicScores([]);
              }}
              className="mt-4 w-full text-center text-sm text-ink-muted hover:text-ink transition-colors py-2"
            >
              Retake the diagnostic
            </button>
          </div>
        )}
      </div>
    </section>
  );
}